import { SETTINGS_SECTIONS } from '../../app/terminology/settings';
// features/vehicles/VehiclePage.tsx
import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import {
    Page,
    IndexTable,
    Card,
    Button,
    useIndexResourceState,
    Text,
    useBreakpoints,
    Select,
    Tag,
    Icon,
    AppProvider,
    FormLayout,
    TextField,
    Banner,
    Checkbox,
    Autocomplete,
    Popover,
    ActionList,
} from '@shopify/polaris';
import { SafeAppBridgeModal as Modal, SafeAppBridgeTitleBar as TitleBar } from '../../app/components/SafeAppBridge';
import { observer } from 'mobx-react-lite';
import { useLocation, useNavigate } from 'react-router-dom';
import { useStore } from '../../app/stores/store';
import agent from '../../app/api/agent';
import type { Vehicle, VehicleCreate, VehicleEdit } from '../../app/models/Vehicle';
import type { FitmentCatalogSettings } from '../../app/models/FitmentCatalog';
import {
    getFitmentFieldEmptyOptionLabel,
    getFitmentFieldValue,
    getLegacyFieldStateKey,
    getLegacyVehicleFieldValue,
    getVisibleFitmentFields,
    hasAnyFitmentFieldValue,
    isFitmentFieldDisabled,
    normalizeFitmentFieldValues,
    normalizeFitmentKey,
    updateFitmentFieldValue,
    type FitmentFieldValues,
} from '../../app/utils/fitmentFields';
import {
    FITMENT_CATALOG_LABEL_CHANGED,
    getCachedFitmentCatalogLabel,
    getStoredFitmentCatalogLabel,
    setCachedFitmentCatalogLabel,
} from '../../app/utils/fitmentCatalogLabel';
import { MiniBanner } from '../components/MiniBanner';
import { DeleteIcon, SearchIcon, FilterIcon, SortIcon } from '@shopify/polaris-icons';
import enTranslations from '@shopify/polaris/locales/en.json';
import { vehicleEditorValues, vehicleEditorPayload, validateVehicleEditor, type VehicleEditorErrors } from './vehicleEditor';
import vehicleEmptyStateImage from '../../assets/dashboard/polaris-fitment.png';
import { catalogColumns, VehicleCatalogRows } from './VehicleCatalogRows';
import './VehiclePage.css';

// Satisfy Polaris' {[key:string]: unknown} constraint
type VehicleRow = Vehicle & { [key: string]: unknown };
type SelectOption = { label: string; value: string };
type FitmentField = ReturnType<typeof getVisibleFitmentFields>[number];

const DEBOUNCE_MS = 500;

const vehicleFieldSortValue = 'fitment-fields asc';

const yearRangeError = (from: string, to: string) => {
    const valid = (value: string) => !value || (/^-?\d+$/.test(value)
        && Number(value) >= -2147483648 && Number(value) <= 2147483647);
    if (!valid(from) || !valid(to)) return 'Enter a whole-number year.';
    return from && to && Number(from) > Number(to)
        ? 'From year must be less than or equal to To year.' : '';
};

const isStandardFitmentField = (field: FitmentField, key: string) =>
    (field.key || '').trim().toLowerCase() === key;

const orderFitmentFieldsWithYearLast = (fields: FitmentField[]) => {
    const yearField = fields.find((field) => isStandardFitmentField(field, 'year'));
    return yearField
        ? [...fields.filter((field) => field !== yearField), yearField]
        : fields;
};

const getAutocompleteOptions = (options: SelectOption[], query: string) => {
    const normalizedQuery = query.trim().toLowerCase();
    const usableOptions = options.filter((option) => option.value);

    return normalizedQuery
        ? usableOptions.filter((option) =>
            option.label.toLowerCase().includes(normalizedQuery)
            || option.value.toLowerCase().includes(normalizedQuery))
        : usableOptions;
};

const FitmentFilterAutocomplete = ({
    label,
    value,
    options,
    disabled,
    loading,
    placeholder,
    onChange,
}: {
    label: string;
    value: string;
    options: SelectOption[];
    disabled?: boolean;
    loading?: boolean;
    placeholder: string;
    onChange: (value: string) => void;
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [query, setQuery] = useState(value);
    const filteredOptions = getAutocompleteOptions(options, query);

    useEffect(() => {
        setQuery(value);
    }, [value]);

    useEffect(() => {
        containerRef.current
            ?.querySelectorAll<HTMLButtonElement>('.Polaris-TextField__ClearButton')
            .forEach((button) => {
                // Match the PLP selector keyboard behavior: tab moves to the
                // next vehicle field instead of stopping on the clear button.
                button.tabIndex = -1;
            });
    }, [query]);

    const textField = (
        <Autocomplete.TextField
            label={label}
            value={query}
            onChange={setQuery}
            autoComplete="off"
            disabled={disabled}
            placeholder={placeholder}
            clearButton={!!query}
            onClearButtonClick={() => {
                setQuery('');
                onChange('');
            }}
        />
    );

    return (
        <div ref={containerRef}>
            <Autocomplete
                options={filteredOptions}
                selected={value ? [value] : []}
                textField={textField}
                onSelect={(selected) => {
                    const nextValue = selected[0] ?? '';
                    setQuery(nextValue);
                    onChange(nextValue);
                }}
                loading={loading}
                emptyState={(
                    <div style={{ padding: 12, color: '#616161', fontSize: 13 }}>
                        No matching values.
                    </div>
                )}
            />
        </div>
    );
};

declare global {
    interface Window {
        shopify?: {
            idToken?: () => Promise<string>;
            toast?: { show?: (msg: string | { message: string; duration?: number }) => void };
        };
    }
}

const showToast = (msg: string) => window.shopify?.toast?.show?.(msg);

const vehicleRequestKey = (args: unknown[]) => JSON.stringify([
    ...args.slice(0, 8),
    Object.entries((args[8] ?? {}) as Record<string, string>)
        .filter(([, value]) => !!value).sort(([a], [b]) => a.localeCompare(b)),
    args[9],
    args[10] || '', args[11] || '',
]);

const VehiclesPage: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const requestedYear = new URLSearchParams(location.search).get('year')?.trim() || '';
    const { vehicleStore } = useStore();
    const { vehicles, loadVehicles: fetchVehicles, page, hasNext, totalCount, loadingInitial } = vehicleStore;
    const [yearView, setYearView] = useState<'ranges' | 'individual'>('ranges');
    const yearViewRef = useRef(yearView);
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [sortOpen, setSortOpen] = useState(false);
    const [filterYearFrom, setFilterYearFrom] = useState('');
    const [filterYearTo, setFilterYearTo] = useState('');
    const filterYearRangeRef = useRef({ from: '', to: '' });
    const rangeError = yearRangeError(filterYearFrom, filterYearTo);
    const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
    const lastVehicleRequest = useRef('');
    const loadVehicles = useCallback((...args: Parameters<typeof fetchVehicles>) => {
        const { from, to } = filterYearRangeRef.current;
        if (yearRangeError(from, to)) return;
        args[9] = yearViewRef.current;
        args[10] = from;
        args[11] = to;
        lastVehicleRequest.current = vehicleRequestKey(args);
        return fetchVehicles(...args);
    }, [fetchVehicles]);
    const [fitmentCatalog, setFitmentCatalog] = useState<FitmentCatalogSettings | null>(null);
    const [vehicleCatalogLabel, setVehicleCatalogLabel] = useState(() => getStoredFitmentCatalogLabel() ?? "");

    const visibleFitmentFields = useMemo(
        () => getVisibleFitmentFields(fitmentCatalog),
        [fitmentCatalog],
    );
    const vehicleListFitmentFields = useMemo(
        () => orderFitmentFieldsWithYearLast(visibleFitmentFields),
        [visibleFitmentFields],
    );
    const legacyVisibleFields = useMemo(
        () => visibleFitmentFields.filter((field) => getLegacyFieldStateKey(field)),
        [visibleFitmentFields],
    );
    const fieldEnabled = useMemo(() => {
        const enabled = new Set(legacyVisibleFields.map((field) => getLegacyFieldStateKey(field)).filter(Boolean));
        return {
            year: enabled.has('year'),
            make: enabled.has('make'),
            model: enabled.has('model'),
            submodel: enabled.has('submodel'),
        };
    }, [legacyVisibleFields]);

    // ---------- Modal open/close ----------
    const [modalOpen, setModalOpen] = useState(false);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [editingIsCustom, setEditingIsCustom] = useState(false);
    const [persistFitments, setPersistFitments] = useState(true);

    const openAdd = useCallback(() => {
        setEditingId(null);
        setEditingIsCustom(false);
        setPersistFitments(true);
        setModalOpen(true);
    }, []);

    const [formValues, setFormValues] = useState<FitmentFieldValues>({});
    const [yearTo, setYearTo] = useState('');
    const [errors, setErrors] = useState<VehicleEditorErrors>({});
    const [submitError, setSubmitError] = useState<string | undefined>();
    const [submitting, setSubmitting] = useState(false);
    const [lastAdded, setLastAdded] = useState<VehicleCreate | null>(null);
    const [lastAddedExists, setLastAddedExists] = useState(false);

    const onEditClick = useCallback((v: Vehicle) => {
        setEditingId(v.id);
        setEditingIsCustom(!!v.isCustom);
        const values = vehicleEditorValues(visibleFitmentFields, v);
        setFormValues(values);
        setYearTo('');
        setPersistFitments(true);
        setErrors({});
        setSubmitError(undefined);
        setModalOpen(true);
    }, [visibleFitmentFields]);

    const resetForm = useCallback(() => {
        setFormValues({});
        setYearTo('');
        setErrors({});
        setSubmitError(undefined);
    }, []);

    const handleClose = useCallback(() => {
        resetForm();
        setLastAdded(null);
        setLastAddedExists(false);
        setEditingId(null);
        setEditingIsCustom(false);
        setModalOpen(false);
    }, [resetForm]);

    const onCopyLast = useCallback(() => {
        if (!lastAdded) return;
        setFormValues({ ...lastAdded.fieldValues });
        setYearTo(lastAdded.yearTo && lastAdded.yearTo !== lastAdded.yearFrom ? String(lastAdded.yearTo) : '');
        setLastAdded(null);
        setLastAddedExists(false);
    }, [lastAdded]);

    const lastAddedLabel = useMemo(() => {
        if (!lastAdded) return '';
        return visibleFitmentFields.map(field => {
            const key = normalizeFitmentKey(field);
            if (key === 'year' && lastAdded.yearTo !== lastAdded.yearFrom)
                return `${lastAdded.yearFrom}-${lastAdded.yearTo}`;
            return lastAdded.fieldValues?.[key] || '';
        }).filter(Boolean).join(', ');
    }, [lastAdded, visibleFitmentFields]);

    const onSave = async () => {
        setSubmitError(undefined);
        const nextErrors = validateVehicleEditor(visibleFitmentFields, formValues, editingIsCustom ? '' : yearTo);
        setErrors(nextErrors);
        if (Object.keys(nextErrors).length > 0) return;

        setSubmitting(true);
        const basePayload = vehicleEditorPayload(visibleFitmentFields, formValues, editingIsCustom ? '' : yearTo);

        try {
            if (editingId) {
                const payload: VehicleEdit = { ...basePayload, isCustom: editingIsCustom, persistFitments };
                await vehicleStore.update(editingId, payload);

                await loadVehicles(
                    1,
                    queryValue,
                    selectedSource[0] || '',
                    sortSelected[0],
                    selectedYear,
                    selectedMake,
                    selectedModel,
                    selectedAssigned[0] || '',
                    selectedFitmentValues,
                );

                showToast('Vehicle saved');
            } else {
                await vehicleStore.create(basePayload);

                await loadVehicles(
                    1,
                    queryValue,
                    selectedSource[0] || '',
                    sortSelected[0],
                    selectedYear,
                    selectedMake,
                    selectedModel,
                    selectedAssigned[0] || '',
                    selectedFitmentValues,
                );

                showToast('Vehicle added');
                setLastAdded(basePayload);
                setLastAddedExists(false);
                resetForm();
            }
        } catch (err: any) {
            if (!editingId && err?.response?.status === 409) {
                await loadVehicles(
                    1,
                    queryValue,
                    selectedSource[0] || '',
                    sortSelected[0],
                    selectedYear,
                    selectedMake,
                    selectedModel,
                    selectedAssigned[0] || '',
                    selectedFitmentValues,
                );

                showToast('Vehicle already exists');
                setLastAdded(basePayload);
                setLastAddedExists(true);
                resetForm();
            } else {
                setSubmitError(err?.response?.data?.message ?? err?.message ?? (editingId ? 'Failed to save vehicle' : 'Failed to create vehicle'));
            }
        } finally {
            setSubmitting(false);
        }
    };

    // ---------- Existing list logic ----------
    const [queryValue, setQueryValue] = useState('');
    const [selectedSource, setSelectedSource] = useState<string[]>([]);
    const [selectedAssigned, setSelectedAssigned] = useState<string[]>([]);
    const [sortSelected, setSortSelected] = useState<string[]>([vehicleFieldSortValue]);
    const [selectedYear, setSelectedYear] = useState(requestedYear);
    const [selectedMake, setSelectedMake] = useState('');
    const [selectedModel, setSelectedModel] = useState('');
    const [selectedAdditionalFields, setSelectedAdditionalFields] = useState<FitmentFieldValues>({});
    const [fieldOptions, setFieldOptions] = useState<Record<string, SelectOption[]>>({});
    const [loadingFieldOptions, setLoadingFieldOptions] = useState(false);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const skipNextDebouncedLoadRef = useRef(false);
    const didInitialLoadRef = useRef(false);
    const selectedFitmentValues = useMemo(
        // Keep legacy state variables for existing sort/filter calls, but also
        // expose the full field map so custom selector fields can participate.
        () => normalizeFitmentFieldValues(vehicleListFitmentFields, {
            ...selectedAdditionalFields,
            year: selectedYear,
            make: selectedMake,
            model: selectedModel,
        }),
        [selectedAdditionalFields, selectedMake, selectedModel, selectedYear, vehicleListFitmentFields],
    );
    const storedFitmentValues = useMemo(
        () => normalizeFitmentFieldValues(vehicleListFitmentFields, {
            ...vehicleStore.fieldValues,
            year: vehicleStore.year,
            make: vehicleStore.make,
            model: vehicleStore.model,
        }),
        [vehicleListFitmentFields, vehicleStore.fieldValues, vehicleStore.year, vehicleStore.make, vehicleStore.model],
    );
    const hasStoredVehicleDropdownFilter = hasAnyFitmentFieldValue(vehicleListFitmentFields, storedFitmentValues);
    const hasVehicleDropdownFilter = hasAnyFitmentFieldValue(vehicleListFitmentFields, selectedFitmentValues);
    const hasActiveVehicleFilters =
        !!filterYearFrom || !!filterYearTo
        || queryValue.trim().length > 0
        || selectedSource.some(Boolean)
        || selectedAssigned.some(Boolean)
        || hasVehicleDropdownFilter
        || vehicleStore.searchTerm.trim().length > 0
        || !!vehicleStore.source
        || !!vehicleStore.assigned
        || hasStoredVehicleDropdownFilter;

    const applySelectedFitmentValues = useCallback((nextValues: FitmentFieldValues) => {
        const normalized = normalizeFitmentFieldValues(vehicleListFitmentFields, nextValues);
        // Split the generic map back into legacy fields plus "everything else".
        // This keeps older vehicle APIs working while the UI can render 4-8
        // merchant-defined fields.
        setSelectedYear(normalized.year || '');
        setSelectedMake(normalized.make || '');
        setSelectedModel(normalized.model || '');
        setSelectedAdditionalFields(
            Object.entries(normalized)
                .filter(([key]) => key !== 'year' && key !== 'make' && key !== 'model')
                .reduce<FitmentFieldValues>((accumulator, [key, value]) => {
                    accumulator[key] = value;
                    return accumulator;
                }, {})
        );
    }, [vehicleListFitmentFields]);

    useEffect(() => {
        // Dashboard review links must start from a clean scope, including when
        // the store still holds filters from an earlier visit to this page.
        setSelectedYear(requestedYear);
        filterYearRangeRef.current = { from: '', to: '' };
        setFilterYearFrom('');
        setFilterYearTo('');
        setQueryValue('');
        setSelectedSource([]);
        setSelectedAssigned([]);
        setSelectedMake('');
        setSelectedModel('');
        setSelectedAdditionalFields({});
        setSortSelected([vehicleFieldSortValue]);
        loadVehicles(1, '', '', vehicleFieldSortValue, requestedYear, '', '', '',
            requestedYear ? { year: requestedYear } : {});
        didInitialLoadRef.current = true;
    }, [loadVehicles, requestedYear]);

    useEffect(() => {
        let cancelled = false;

        const loadFitmentCatalog = async () => {
            try {
                const catalog = await agent.Settings.fitmentCatalog();
                if (!cancelled) {
                    setFitmentCatalog(catalog);
                    setVehicleCatalogLabel(setCachedFitmentCatalogLabel(catalog.name));
                }
            } catch {
                // The field helper falls back to the default YMMS shape when
                // settings cannot be loaded, keeping the page usable.
                if (!cancelled) {
                    setFitmentCatalog(null);
                    setVehicleCatalogLabel(getCachedFitmentCatalogLabel());
                }
            }
        };

        const handleLabelChange = (event: Event) => {
            const nextLabel = (event as CustomEvent<string>).detail;
            if (nextLabel) setVehicleCatalogLabel(nextLabel);
        };

        window.addEventListener(FITMENT_CATALOG_LABEL_CHANGED, handleLabelChange);
        loadFitmentCatalog();
        return () => {
            cancelled = true;
            window.removeEventListener(FITMENT_CATALOG_LABEL_CHANGED, handleLabelChange);
        };
    }, []);

    useEffect(() => {
        if (!didInitialLoadRef.current) return;
        if (skipNextDebouncedLoadRef.current) {
            skipNextDebouncedLoadRef.current = false;
            return;
        }

        if (debounceRef.current) clearTimeout(debounceRef.current);

        debounceRef.current = setTimeout(() => {
            const args: Parameters<typeof fetchVehicles> = [
                1,
                queryValue,
                selectedSource[0] || '',
                sortSelected[0],
                selectedYear,
                selectedMake,
                selectedModel,
                selectedAssigned[0] || '',
                selectedFitmentValues,
                yearViewRef.current,
                filterYearFrom,
                filterYearTo,
            ];
            // Settings and state initialization can rerender without changing
            // the query. Do not restart the same expensive catalog request.
            if (vehicleRequestKey(args) !== lastVehicleRequest.current) loadVehicles(...args);
        }, DEBOUNCE_MS);

        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
    }, [queryValue, selectedSource, selectedAssigned, sortSelected, selectedYear, selectedMake, selectedModel, selectedFitmentValues, filterYearFrom, filterYearTo, loadVehicles]);

    useEffect(() => {
        // If settings disable a legacy field, clear its state so hidden values
        // do not continue filtering the vehicle list.
        if (!fieldEnabled.year) {
            setSelectedYear('');
            setFilterYearFrom('');
            setFilterYearTo('');
            filterYearRangeRef.current = { from: '', to: '' };
        }
        if (!fieldEnabled.make) setSelectedMake('');
        if (!fieldEnabled.model) setSelectedModel('');
    }, [fieldEnabled.make, fieldEnabled.model, fieldEnabled.year]);

    useEffect(() => {
        let cancelled = false;

        const loadOptions = async () => {
            if (!filtersOpen) return;
            if (vehicleListFitmentFields.length === 0) {
                setFieldOptions({});
                return;
            }

            setLoadingFieldOptions(true);
            try {
                // The admin selector uses every enabled field. Required/search rules
                // control validation and disabled states, not whether the field appears.
                const result = await vehicleStore.filterOptions(
                    selectedFitmentValues.year || '',
                    selectedFitmentValues.make || '',
                    selectedSource[0] || '',
                    selectedFitmentValues,
                    vehicleListFitmentFields.map((field) => normalizeFitmentKey(field)),
                );

                if (cancelled) return;

                const nextOptions = vehicleListFitmentFields.reduce<Record<string, SelectOption[]>>((accumulator, field) => {
                    const key = normalizeFitmentKey(field);
                    const values = result.optionsByField?.[key] ?? [];
                    accumulator[key] = [
                        { label: getFitmentFieldEmptyOptionLabel(field, 'all'), value: '' },
                        ...values.map((value) => ({ label: value, value })),
                    ];
                    return accumulator;
                }, {});

                setFieldOptions(nextOptions);
            } catch {
                if (!cancelled) setFieldOptions({});
            } finally {
                if (!cancelled) setLoadingFieldOptions(false);
            }
        };

        loadOptions();
        return () => {
            cancelled = true;
        };
    }, [filtersOpen, fitmentCatalog, selectedFitmentValues, selectedSource, vehicleStore, vehicleListFitmentFields]);

    // Vehicle dropdown filters are applied server-side so the list remains paged.
    // Do not pull all pages into the client; the API should return one page of
    // filtered results at a time.

    const handleQueryChange = useCallback((v: string) => setQueryValue(v), []);
    const handleQueryClear = useCallback(() => setQueryValue(''), []);
    const onSourceChange = useCallback((v: string[]) => setSelectedSource(v), []);
    const onAssignedChange = useCallback((v: string[]) => setSelectedAssigned(v), []);
    const changeYearRange = (from: string, to: string) => {
        filterYearRangeRef.current = { from, to };
        setFilterYearFrom(from);
        setFilterYearTo(to);
        setSelectedYear('');
        if ((from || to) && !yearRangeError(from, to)) {
            yearViewRef.current = 'individual';
            setYearView('individual');
        }
    };

    const sortOptions: SelectOption[] = [
        { label: `${SETTINGS_SECTIONS.selectorFields}: A–Z`, value: vehicleFieldSortValue },
        { label: `${SETTINGS_SECTIONS.selectorFields}: Z–A`, value: 'fitment-fields desc' },
        ...(fieldEnabled.year ? [
            { label: 'Year: newest first', value: 'year desc' },
            { label: 'Year: oldest first', value: 'year asc' },
        ] : []),
        { label: 'Assigned products: fewest', value: 'assigned asc' },
        { label: 'Assigned products: most', value: 'assigned desc' },
    ];

    const onClearAll = useCallback(() => {
        skipNextDebouncedLoadRef.current = true;
        setQueryValue('');
        setSelectedSource([]);
        setSelectedAssigned([]);
        setSortSelected([vehicleFieldSortValue]);
        setSelectedYear('');
        setSelectedMake('');
        setSelectedModel('');
        setSelectedAdditionalFields({});

        filterYearRangeRef.current = { from: '', to: '' };
        setFilterYearFrom('');
        setFilterYearTo('');

        loadVehicles(1, '', '', vehicleFieldSortValue, '', '', '', '', {});
    }, [loadVehicles]);


    const hasPrevious = page > 1;

    const onNext = useCallback(() => {
        if (hasNext) {
            loadVehicles(
                page + 1,
                queryValue,
                selectedSource[0] || '',
                sortSelected[0],
                selectedYear,
                selectedMake,
                selectedModel,
                selectedAssigned[0] || '',
                selectedFitmentValues,
            );
        }
    }, [hasNext, loadVehicles, page, queryValue, selectedSource, selectedAssigned, sortSelected, selectedYear, selectedMake, selectedModel, selectedFitmentValues]);

    const onPrevious = useCallback(() => {
        if (hasPrevious) {
            loadVehicles(
                page - 1,
                queryValue,
                selectedSource[0] || '',
                sortSelected[0],
                selectedYear,
                selectedMake,
                selectedModel,
                selectedAssigned[0] || '',
                selectedFitmentValues,
            );
        }
    }, [hasPrevious, loadVehicles, page, queryValue, selectedSource, selectedAssigned, sortSelected, selectedYear, selectedMake, selectedModel, selectedFitmentValues]);

    // Never allow selection of stale rows while new year bounds are pending,
    // invalid, or failed. Bulk deletion must match the years being reviewed.
    const rangeResultsPending = !!rangeError
        || filterYearFrom !== (vehicleStore.yearFrom || '')
        || filterYearTo !== (vehicleStore.yearTo || '');
    const filteredVehicles = useMemo(() => {
        return !rangeResultsPending && !loadingInitial && Array.isArray(vehicles) ? vehicles : [];
    }, [vehicles, rangeResultsPending, loadingInitial]);

    const rows: VehicleRow[] = filteredVehicles.slice() as VehicleRow[];
    const showCreateVehicleEmptyState =
        didInitialLoadRef.current
        && !loadingInitial
        && !vehicleStore.loadError
        && rows.length === 0
        && totalCount === 0
        && !hasActiveVehicleFilters;
    const { selectedResources, allResourcesSelected, handleSelectionChange, clearSelection } =
        useIndexResourceState<VehicleRow>(rows, { resourceIDResolver: (v) => String(v.id) });
    const selectionScope = JSON.stringify([page, yearView, queryValue, selectedSource, selectedAssigned, sortSelected, selectedFitmentValues, filterYearFrom, filterYearTo]);
    useEffect(() => {
        clearSelection();
        setExpandedGroups(new Set());
    }, [clearSelection, selectionScope]);
    const displayedColumns = catalogColumns(vehicleListFitmentFields);
    const combinedIdentity = displayedColumns.length < vehicleListFitmentFields.length;
    const activeFilterCount = Object.values(selectedFitmentValues).filter(Boolean).length
        + selectedSource.length + selectedAssigned.length + Number(!!(filterYearFrom || filterYearTo));
    const isCondensed = useBreakpoints().mdDown;
    const openAssignedProducts = useCallback((vehicle: Vehicle) => {
        const params = new URLSearchParams();
        const fieldValues = vehicleListFitmentFields.reduce<FitmentFieldValues>((accumulator, field) => {
            const key = normalizeFitmentKey(field);
            const value = getLegacyVehicleFieldValue(vehicle, field);
            if (key && value && value !== '-') {
                accumulator[key] = value;
            }
            return accumulator;
        }, {});
        const year = fieldValues.year || '';
        const make = fieldValues.make || '';
        const model = fieldValues.model || '';

        if (year) params.set('year', year);
        if (make) params.set('make', make);
        if (model) params.set('model', model);
        if (Object.keys(fieldValues).length > 0) {
            params.set('fieldValues', JSON.stringify(fieldValues));
        }
        params.set('mappedOnly', 'true');

        navigate(`/products?${params.toString()}`);
    }, [navigate, vehicleListFitmentFields]);
    // ---------- BULK ACTIONS ----------
    const selectedCount = allResourcesSelected ? totalCount : selectedResources.length;

    const [deleteOpen, setDeleteOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [deleteInfo, setDeleteInfo] = useState<{ hasCustom: boolean; hasStandard: boolean; customCount: number }>({
        hasCustom: false,
        hasStandard: false,
        customCount: 0,
    });

    const onBulkDeleteClick = useCallback(() => {
        if (rangeResultsPending || loadingInitial) return;
        const count = allResourcesSelected ? totalCount : selectedResources.length;
        if (count < 1) return;

        if (allResourcesSelected) {
            const hasStandard = selectedSource[0] !== 'Custom';
            const hasCustom = selectedSource[0] !== 'System';
            setDeleteInfo({ hasStandard, hasCustom, customCount: hasCustom ? count : 0 });
        } else {
            const selectedVehicles = rows.filter((v) => selectedResources.includes(String(v.id)));
            const customVehicles = selectedVehicles.filter((v) => v.isCustom);

            setDeleteInfo({
                hasStandard: selectedVehicles.length > customVehicles.length,
                hasCustom: customVehicles.length > 0,
                customCount: customVehicles.length,
            });
        }

        setDeleteOpen(true);
    }, [allResourcesSelected, totalCount, selectedResources, rows, selectedSource, rangeResultsPending, loadingInitial]);

    const confirmDelete = useCallback(async () => {
        if (rangeResultsPending || loadingInitial) return;
        setDeleting(true);

        try {
            if (typeof (vehicleStore as any).bulkDelete === 'function') {
                if (allResourcesSelected) {
                    await (vehicleStore as any).bulkDelete({
                        ids: null,
                        scope: {
                            query: queryValue,
                            source: 'Custom',
                            sort: sortSelected[0],
                            year: selectedYear,
                            yearFrom: filterYearFrom ? Number(filterYearFrom) : undefined,
                            yearTo: filterYearTo ? Number(filterYearTo) : undefined,
                            make: selectedMake,
                            model: selectedModel,
                            fieldValues: selectedFitmentValues,
                            assigned: selectedAssigned[0] || '',
                        },
                    });
                } else {
                    const ids = rows
                        .filter((v) => selectedResources.includes(String(v.id)) && v.isCustom)
                        .map((v) => Number(v.id));

                    await (vehicleStore as any).bulkDelete({ ids });
                }
            } else {
                const ids = allResourcesSelected
                    ? []
                    : rows
                        .filter((v) => selectedResources.includes(String(v.id)) && v.isCustom)
                        .map((v) => Number(v.id));

                await Promise.all(ids.map((id) => (vehicleStore as any).delete?.(id)));
            }

            showToast('Vehicles deleted');
            setDeleteOpen(false);
            clearSelection();

            await loadVehicles(
                1,
                queryValue,
                selectedSource[0] || '',
                sortSelected[0],
                selectedYear,
                selectedMake,
                selectedModel,
                selectedAssigned[0] || '',
                selectedFitmentValues,
            );
        } catch (e: any) {
            showToast(e?.message ?? 'Failed to delete');
        } finally {
            setDeleting(false);
        }
    }, [
        allResourcesSelected,
        selectedResources,
        rows,
        vehicleStore,
        clearSelection,
        loadVehicles,
        queryValue,
        selectedSource,
        selectedAssigned,
        sortSelected,
        selectedYear,
        selectedMake,
        selectedModel,
        selectedFitmentValues,
        filterYearFrom,
        filterYearTo,
        rangeResultsPending,
        loadingInitial,
    ]);

    const bulkActions = [
        { icon: DeleteIcon, content: 'Delete', destructive: true, onAction: onBulkDeleteClick, disabled: selectedCount < 1 },
    ];

    return (
        <>
            {/* App Bridge React Modal with local Polaris AppProvider so portal components work */}
            <Modal id="add-vehicle" open={modalOpen} onHide={handleClose}>
                <AppProvider i18n={enTranslations}>
                    <div style={{ padding: 16 }} onInput={() => { if (lastAdded) { setLastAdded(null); setLastAddedExists(false); } }}>
                        {submitError && (
                            <Banner tone="critical">{submitError}</Banner>
                        )}

                        {editingId && !editingIsCustom && (
                            <div style={{ backgroundColor: '#f6f6f7', padding: 12, marginTop: 8, marginBottom: 16 }}>
                                <Text as="p">This is a system vehicle and cannot be edited. Saving will create a new vehicle.</Text>
                                <Checkbox
                                    label="Persist saved fitments"
                                    checked={persistFitments}
                                    onChange={(v: boolean) => setPersistFitments(v)}
                                />
                            </div>
                        )}

                        <FormLayout>
                            {!editingId && lastAdded && (
                                <FormLayout.Group>
                                    <MiniBanner
                                        tone={lastAddedExists ? 'info' : 'success'}
                                        title={lastAddedExists ? 'Vehicle already exists' : 'Vehicle added'}
                                        onDismiss={() => { setLastAdded(null); setLastAddedExists(false); }}
                                        actions={[
                                            { content: 'Duplicate', onAction: onCopyLast },
                                        ]}
                                    >
                                        <b>{lastAddedLabel}</b>
                                    </MiniBanner>
                                </FormLayout.Group>
                            )}

                            {visibleFitmentFields.map(field => {
                                const key = normalizeFitmentKey(field);
                                const input = (
                                    <TextField
                                        key={key}
                                        label={field.label}
                                        requiredIndicator={field.isRequired}
                                        value={formValues[key] || ''}
                                        onChange={value => {
                                            setFormValues(previous => ({ ...previous, [key]: value }));
                                            setErrors(previous => ({ ...previous, [key]: undefined }));
                                        }}
                                        type={field.dataType === 'Date' ? 'date' : 'text'}
                                        inputMode={key === 'year' || field.dataType === 'Number' ? 'numeric' : field.dataType === 'Decimal' ? 'decimal' : 'text'}
                                        maxLength={Math.min(field.maxLength ?? 400, 400)}
                                        autoComplete="off"
                                        placeholder={field.placeholder || undefined}
                                        helpText={field.helpText || undefined}
                                        error={errors[key]}
                                    />
                                );
                                return key === 'year' && !editingIsCustom ? (
                                    <FormLayout.Group key={key} condensed>
                                        {input}
                                        <TextField
                                            label="End year (optional)"
                                            value={yearTo}
                                            onChange={value => {
                                                setYearTo(value);
                                                setErrors(previous => ({ ...previous, yearTo: undefined }));
                                            }}
                                            inputMode="numeric"
                                            autoComplete="off"
                                            error={errors.yearTo}
                                        />
                                    </FormLayout.Group>
                                ) : input;
                            })}
                        </FormLayout>
                    </div>
                </AppProvider>

                <TitleBar
                    title={editingId ? (editingIsCustom ? 'Edit vehicle' : 'Edit system vehicle') : 'Add vehicle'}>
                    <button onClick={handleClose} disabled={submitting} style={{ marginRight: 8 }}>Cancel</button>
                    <button variant="primary" onClick={onSave} disabled={submitting} style={{ marginRight: 8 }}>Save</button>
                </TitleBar>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal id="delete-vehicles" open={deleteOpen} onHide={() => setDeleteOpen(false)}>
                <AppProvider i18n={enTranslations}>
                    <div style={{ padding: 16 }}>
                        <MiniBanner tone="critical" title="Delete vehicles">
                            <p style={{ marginTop: 8 }}>
                                {deleteInfo.hasStandard && !deleteInfo.hasCustom && (
                                    <>System vehicle data cannot be deleted.</>
                                )}
                                {deleteInfo.hasStandard && deleteInfo.hasCustom && (
                                    <>System vehicle data cannot be deleted. Only user-added vehicles (and their associated fitments) will be deleted. This action cannot be undone.</>
                                )}
                                {!deleteInfo.hasStandard && deleteInfo.hasCustom && (
                                    allResourcesSelected
                                        ? <>You’re about to delete <b>ALL {totalCount}</b> vehicles (and their associated fitments) that match the current filters. This cannot be undone.</>
                                        : <>You’re about to delete <b>{deleteInfo.customCount}</b> selected vehicle(s) (and their associated fitments). This cannot be undone.</>
                                )}
                            </p>
                        </MiniBanner>
                    </div>
                </AppProvider>

                <TitleBar title="Delete vehicles">
                    <button onClick={() => setDeleteOpen(false)} disabled={deleting}>Cancel</button>
                    <button variant="primary" onClick={confirmDelete} disabled={deleting || !deleteInfo.hasCustom}>Delete</button>
                </TitleBar>
            </Modal>

            <Page
                fullWidth
                title={vehicleCatalogLabel || "\u00a0"}
                primaryAction={{ content: 'Add vehicle', onAction: openAdd }}
                secondaryActions={[
                    {
                        content: 'Manage fields',
                        onAction: () => navigate('/storefront?section=selector-fields'),
                    },
                ]}
            >
                <div className="pf-vehicle-catalog">
                <Card>
                    <div className="pf-vehicle-toolbar">
                        <div className="pf-vehicle-search">
                            <TextField label="Search vehicles" labelHidden value={queryValue}
                                onChange={handleQueryChange} onClearButtonClick={handleQueryClear}
                                clearButton autoComplete="off" placeholder="Search vehicles"
                                prefix={<Icon source={SearchIcon} />} />
                        </div>
                        <Button icon={FilterIcon} onClick={() => setFiltersOpen(open => !open)}
                            pressed={filtersOpen} ariaExpanded={filtersOpen} ariaControls="vehicle-catalog-filters">
                            {activeFilterCount ? `Filters (${activeFilterCount})` : 'Filters'}
                        </Button>
                        <div className="pf-vehicle-sort">
                            <Popover active={sortOpen} onClose={() => setSortOpen(false)}
                                activator={<Button icon={SortIcon} onClick={() => setSortOpen(open => !open)}
                                    accessibilityLabel={`Sort vehicles: ${sortOptions.find(option => option.value === sortSelected[0])?.label}`} disclosure>Sort</Button>}>
                                <ActionList actionRole="menuitem" items={sortOptions.map(option => ({
                                    content: option.label, active: option.value === sortSelected[0],
                                    onAction: () => { setSortSelected([option.value]); setSortOpen(false); },
                                }))} />
                            </Popover>
                        </div>
                    </div>
                    {activeFilterCount > 0 && <div className="pf-vehicle-applied">
                        {vehicleListFitmentFields.filter(field => getFitmentFieldValue(selectedFitmentValues, field)).map(field => (
                            <Tag key={normalizeFitmentKey(field)} onRemove={() => applySelectedFitmentValues(
                                updateFitmentFieldValue(vehicleListFitmentFields, selectedFitmentValues, field, ''))}>
                                {`${field.label}: ${getFitmentFieldValue(selectedFitmentValues, field)}`}
                            </Tag>
                        ))}
                        {(filterYearFrom || filterYearTo) && <Tag onRemove={() => changeYearRange('', '')}>
                            {`Years: ${filterYearFrom && filterYearTo ? `${filterYearFrom}–${filterYearTo}`
                                : filterYearFrom ? `${filterYearFrom} onward` : `Up to ${filterYearTo}`}`}
                        </Tag>}
                        {selectedSource.length > 0 && <Tag onRemove={() => setSelectedSource([])}>
                            {`Source: ${selectedSource[0] === 'Custom' ? 'User-added' : selectedSource[0]}`}
                        </Tag>}
                        {selectedAssigned.length > 0 && <Tag onRemove={() => setSelectedAssigned([])}>
                            {selectedAssigned[0] === 'with' ? 'With assigned products' : 'Without assigned products'}
                        </Tag>}
                        <Button variant="plain" onClick={onClearAll}>Clear all</Button>
                    </div>}
                    {filtersOpen && <div id="vehicle-catalog-filters" className="pf-vehicle-filters">
                        <div className="pf-vehicle-filter-heading">
                            <Text as="h2" variant="headingSm">Filter vehicles</Text>
                            <Button onClick={() => setFiltersOpen(false)}>Done</Button>
                        </div>
                        <div className="pf-vehicle-filter-grid">
                            {vehicleListFitmentFields.filter(field => !isStandardFitmentField(field, 'year')).map(field => {
                                const key = normalizeFitmentKey(field);
                                return <FitmentFilterAutocomplete key={key} label={field.label}
                                    options={fieldOptions[key] ?? []}
                                    value={getFitmentFieldValue(selectedFitmentValues, field)}
                                    disabled={isFitmentFieldDisabled(vehicleListFitmentFields, selectedFitmentValues, field, loadingFieldOptions)}
                                    loading={loadingFieldOptions}
                                    placeholder={getFitmentFieldEmptyOptionLabel(field, 'all')}
                                    onChange={value => {
                                        const nextValues = updateFitmentFieldValue(vehicleListFitmentFields, selectedFitmentValues, field, value);
                                        skipNextDebouncedLoadRef.current = true;
                                        applySelectedFitmentValues(nextValues);
                                        loadVehicles(1, queryValue, selectedSource[0] || '', sortSelected[0],
                                            nextValues.year || '', nextValues.make || '', nextValues.model || '',
                                            selectedAssigned[0] || '', normalizeFitmentFieldValues(vehicleListFitmentFields, nextValues));
                                    }} />;
                            })}
                        </div>
                        {fieldEnabled.year && <fieldset className="pf-vehicle-year-range">
                            <legend>Year range</legend>
                            <div className="pf-vehicle-year-inputs">
                                <TextField label="From year" type="number" step={1} autoComplete="off"
                                    placeholder="Any" value={filterYearFrom || selectedYear}
                                    onChange={value => changeYearRange(value, filterYearTo || selectedYear)} />
                                <TextField label="To year" type="number" step={1} autoComplete="off"
                                    placeholder="Any" value={filterYearTo || selectedYear}
                                    onChange={value => changeYearRange(filterYearFrom || selectedYear, value)} />
                            </div>
                            <Text as="p" tone="subdued">Includes both years. Leave either end blank for no limit.</Text>
                            {rangeError && <div role="alert"><Text as="p" tone="critical">{rangeError}</Text></div>}
                        </fieldset>}
                        <details className="pf-vehicle-more-filters">
                            <summary>More filters{selectedSource.length + selectedAssigned.length > 0
                                ? ` (${selectedSource.length + selectedAssigned.length})` : ''}</summary>
                            <div className="pf-vehicle-filter-extras">
                            <Select label="Data source" options={[
                                {label: 'All sources', value: ''}, {label: 'System', value: 'System'},
                                {label: 'User-added', value: 'Custom'}, {label: 'ATD', value: 'ATD'}, {label: 'Turn14', value: 'Turn14'},
                            ]} value={selectedSource[0] || ''} onChange={value => onSourceChange(value ? [value] : [])} />
                            <Select label="Assigned products" options={[
                                {label: 'All vehicles', value: ''}, {label: 'With assigned products', value: 'with'},
                                {label: 'Without assigned products', value: 'without'},
                            ]} value={selectedAssigned[0] || ''} onChange={value => onAssignedChange(value ? [value] : [])} />
                            </div>
                        </details>
                    </div>}
                    <div className="pf-vehicle-viewbar">
                        <Text as="p" tone="subdued">
                            {rangeError ? 'Check the year range to view vehicles.' : vehicleStore.loadError ? 'Vehicles could not be loaded.' : loadingInitial || rangeResultsPending
                                ? 'Loading vehicles…' : `${totalCount.toLocaleString()} vehicle ${totalCount === 1 ? 'record' : 'records'}${yearView === 'ranges' && vehicleStore.groupCount != null ? ` · ${vehicleStore.groupCount.toLocaleString()} ${vehicleStore.groupCount === 1 ? 'group' : 'groups'}` : ''}`}
                        </Text>
                        {fieldEnabled.year && <div className="pf-vehicle-views" role="group" aria-label="Year display">
                            {(['ranges', 'individual'] as const).map(view => <button key={view} type="button"
                                aria-pressed={yearView === view} onClick={() => {
                                    if (yearView === view) return;
                                    yearViewRef.current = view;
                                    setYearView(view);
                                    loadVehicles(1, queryValue, selectedSource[0] || '', sortSelected[0],
                                        selectedYear, selectedMake, selectedModel, selectedAssigned[0] || '', selectedFitmentValues);
                                }}>{view === 'ranges' ? 'Year ranges' : 'Individual years'}</button>)}
                        </div>}
                    </div>
                    {vehicleStore.loadError && <Banner tone="critical" action={{ content: 'Try again', onAction: () => loadVehicles(
                        1, queryValue, selectedSource[0] || '', sortSelected[0], selectedYear, selectedMake,
                        selectedModel, selectedAssigned[0] || '', selectedFitmentValues) }}>
                        {vehicleStore.loadError}
                    </Banner>}
                    <IndexTable
                        condensed={isCondensed}
                        resourceName={{ singular: 'vehicle', plural: 'vehicles' }}
                        itemCount={rows.length}
                        selectedItemsCount={allResourcesSelected ? 'All' : selectedResources.length}
                        onSelectionChange={handleSelectionChange}
                        bulkActions={bulkActions}
                        emptyState={loadingInitial || rangeResultsPending || vehicleStore.loadError ? <div /> : showCreateVehicleEmptyState ? (
                            <div className="pf-vehicle-empty-state">
                                <img
                                    className="pf-vehicle-empty-state__image"
                                    src={vehicleEmptyStateImage}
                                    alt=""
                                    aria-hidden="true"
                                />
                                <Text as="h2" variant="headingMd">
                                    Add your first vehicle
                                </Text>
                                <div className="pf-vehicle-empty-state__copy">
                                    <Text as="p" tone="subdued">
                                        Add vehicles manually or import them from a file to start your fitment catalog.
                                    </Text>
                                </div>
                                <div className="pf-vehicle-empty-state__actions">
                                    <Button variant="secondary" onClick={() => navigate('/imports')}>
                                        Import vehicles
                                    </Button>
                                    <Button variant="primary" onClick={openAdd}>
                                        Add vehicle
                                    </Button>
                                </div>
                            </div>
                        ) : undefined}
                        headings={[
                            ...displayedColumns.map(field => ({ title: combinedIdentity && normalizeFitmentKey(field) === 'model'
                                ? 'Vehicle' : yearView === 'ranges' && normalizeFitmentKey(field) === 'year' ? 'Years' : field.label })),
                            { title: 'Assigned products' }, { title: '' },
                        ] as any}
                        pagination={hasNext || hasPrevious ? { hasNext, hasPrevious, onNext, onPrevious } : undefined}
                        loading={loadingInitial}
                    >
                        <VehicleCatalogRows vehicles={filteredVehicles} fields={vehicleListFitmentFields}
                            groups={yearView === 'ranges' ? vehicleStore.yearGroups : null}
                            expanded={expandedGroups} onToggle={key => setExpandedGroups(previous => {
                                const next = new Set(previous);
                                if (next.has(key)) next.delete(key); else next.add(key);
                                return next;
                            })}
                            selected={selectedResources} onSelect={(id, checked) => handleSelectionChange('single' as Parameters<typeof handleSelectionChange>[0], checked, id)}
                            onEdit={onEditClick} onProducts={openAssignedProducts} />
                    </IndexTable>
                </Card>
                </div>
            </Page>
        </>
    );
};

export default observer(VehiclesPage);
